// src/secure/surveys/SurveyCreate.tsx
import React, { Component, SyntheticEvent } from 'react';
import Wrapper from "../Wrapper";
import axios from 'axios';
import { Redirect } from 'react-router-dom';

type Country = { id: number; name: string };
type OdkProject = { id: number; name: string; archived?: boolean; createdAt?: string };

type OdkForm = {
    xmlFormId: string;
    name?: string;
    version?: string;
    state?: string;
    createdAt?: string;
    url?: string | null;
    links?: { public_links?: Array<{ access_url?: string | null }> };
};

const publicFormUrl = (form: OdkForm): string | undefined =>
    form.url || form.links?.public_links?.find(link => link.access_url)?.access_url || undefined;

const PROJECTS_URL = "surveys/api/odk/projects";
const FORMS_URL = (projectId: number | string) => `surveys/api/odk/projects/${projectId}/forms`;

type State = {
    redirect: boolean;
    loading: boolean;
    error?: string;

    // Countries
    countries: Country[];
    country: string;

    // ODK projects/forms
    projects: OdkProject[];
    project_id: string;     // bound to <select>
    forms: OdkForm[];
    formsLoading: boolean;
    formsError?: string;

    // Survey fields
    name: string;
    link: string;
};

class SurveyCreate extends Component<{}, State> {
    state: State = {
        redirect: false,
        loading: true,
        error: undefined,

        countries: [],
        country: "",

        projects: [],
        project_id: "",
        forms: [],
        formsLoading: false,
        formsError: undefined,

        name: '',
        link: '',
    };

    async componentDidMount() {
        try {
            // Load countries and ODK projects in parallel
            const [countriesRes, projectsRes] = await Promise.all([
                axios.get('selections/country/'),
                axios.get(PROJECTS_URL),
            ]);

            // Countries: API gives [{ "1":"Burundi" }, ...] → normalize
            const rawCountries: Array<Record<string, string>> = countriesRes?.data?.data || [];
            const countries: Country[] = rawCountries.flatMap(obj =>
                Object.entries(obj).map(([id, name]) => ({ id: parseInt(id, 10), name }))
            );

            // Projects
            const projects: OdkProject[] = (projectsRes?.data || []).slice().sort(
                (a: OdkProject, b: OdkProject) => Number(a.id) - Number(b.id)
            );

            const country = countries.length ? countries[0].name : "";
            const project_id = projects.length ? String(projects[0].id) : "";

            this.setState(
                { countries, projects, country, project_id, loading: false },
                () => {
                    if (project_id) this.fetchForms(project_id);
                }
            );
        } catch (err: any) {
            this.setState({
                loading: false,
                error: err?.response?.data?.message || "Failed to load setup data (countries/projects).",
            });
        }
    }

    fetchForms = async (projectId: string) => {
        this.setState({ formsLoading: true, formsError: undefined, forms: [] });
        try {
            const res = await axios.get(FORMS_URL(projectId));
            // Forms include public access URLs from ODK Central.
            const forms: OdkForm[] = res?.data || [];
            forms.sort((a, b) => (a.name || a.xmlFormId).localeCompare(b.name || b.xmlFormId));
            this.setState({ forms, formsLoading: false });
        } catch (err: any) {
            this.setState({
                formsLoading: false,
                formsError: err?.response?.data?.message || "Failed to load forms for the selected project.",
            });
        }
    };

    onProjectChange = (projectIdStr: string) => {
        this.setState({ project_id: projectIdStr, link: "" }, () => {
            if (projectIdStr) this.fetchForms(projectIdStr);
        });
    };

    useFormLink = (form: OdkForm) => {
        const url = publicFormUrl(form);
        if (url) {
            this.setState({ link: url, error: undefined });
        } else {
            this.setState({ error: 'This form has no public access link. Create one in ODK Central first.' });
        }
    };

    submit = async (e: SyntheticEvent) => {
        e.preventDefault();
        const { name, project_id, country, link } = this.state;

        if (!project_id) {
            this.setState({ error: "Please select an ODK project." });
            return;
        }

        try {
            await axios.post('surveys', {
                name,
                project_id,        // if your API expects a number: Number(project_id)
                country,
                link,
            });
            this.setState({ redirect: true });
        } catch (err: any) {
            this.setState({
                error: err?.response?.data?.message || "Failed to create survey.",
            });
        }
    };

    render() {
        if (this.state.redirect) {
            return <Redirect to={'/surveys'} />;
        }

        const {
            loading, error,
            countries, country,
            projects, project_id,
            forms, formsLoading, formsError,
            name, link,
        } = this.state;

        return (
            <Wrapper>
                <form onSubmit={this.submit}>
                    <h3 className="mb-3">Create Survey</h3>

                    {error && (
                        <div className="alert alert-danger" role="alert">
                            {error}
                        </div>
                    )}

                    {/* Name */}
                    <div className="form-group">
                        <label>Name</label>
                        <input
                            type="text"
                            className="form-control"
                            name="name"
                            required
                            value={name}
                            onChange={e => this.setState({ name: e.target.value })}
                            disabled={loading}
                        />
                    </div>

                    {/* ODK Project */}
                    <div className="form-group">
                        <label>ODK Project</label>
                        <select
                            className="form-control"
                            name="project_id"
                            value={project_id}
                            required
                            disabled={loading || projects.length === 0}
                            onChange={e => this.onProjectChange(e.target.value)}
                        >
                            <option value="" disabled>
                                {loading ? "Loading projects..." : "Select Project"}
                            </option>
                            {projects.map((p) => (
                                <option key={p.id} value={String(p.id)}>
                                    {p.id} — {p.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Country */}
                    <div className="form-group">
                        <label>Country</label>
                        <select
                            className="form-control"
                            name="country"
                            value={country}
                            required
                            disabled={loading || countries.length === 0}
                            onChange={e => this.setState({ country: e.target.value })}
                        >
                            <option value="" disabled>
                                {loading ? "Loading countries..." : "Select Country"}
                            </option>
                            {countries.map(c => (
                                <option key={c.id} value={c.name}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Link (auto-filled by “Use link” buttons below, but editable) */}
                    <div className="form-group">
                        <label>Link</label>
                        <input
                            type="text"
                            className="form-control"
                            name="link"
                            required
                            value={link}
                            onChange={e => this.setState({ link: e.target.value })}
                            disabled={loading}
                        />
                        <small className="form-text text-muted">
                            Choose a form below and click “Use link” to add its public access URL.
                        </small>
                    </div>

                    <button
                        className="btn btn-outline-secondary"
                        type="submit"
                        disabled={loading}
                    >
                        Save
                    </button>
                </form>

                {/* Forms list for the selected ODK project */}
                <div className="mt-4">
                    <h5 className="mb-2">Forms in selected ODK project</h5>

                    {formsLoading && <p>Loading forms…</p>}
                    {formsError && <p style={{ color: 'crimson' }}>Error: {formsError}</p>}

                    {!formsLoading && !formsError && project_id && (
                        <>
                            {forms.length === 0 ? (
                                <p>No forms found for this project.</p>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-striped table-sm">
                                        <thead>
                                        <tr>
                                            <th>xmlFormId</th>
                                            <th>Name</th>
                                            <th>Version</th>
                                            <th>Created</th>
                                            <th>Link</th>
                                            <th>Action</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {forms.map((f) => {
                                            const url = publicFormUrl(f);

                                            return (
                                                <tr key={f.xmlFormId}>
                                                    <td>{f.xmlFormId}</td>
                                                    <td>{f.name || ''}</td>
                                                    <td>{f.version || ''}</td>
                                                    <td>{f.createdAt ? new Date(f.createdAt).toLocaleString() : ''}</td>
                                                    <td>
                                                        {url ? (
                                                            <a href={url} target="_blank" rel="noopener noreferrer">Open public form</a>
                                                        ) : 'No public access link'}
                                                    </td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => this.useFormLink(f)}
                                                            disabled={!url}
                                                        >
                                                            Use link
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Wrapper>
        );
    }
}

export default SurveyCreate;
